/**
 * Loyalty Heading Block - Frontend View
 *
 * This component renders on the actual frontend (not the editor).
 * It uses the enrollment store to determine the user's actual enrollment status
 * and renders the appropriate heading text with variable replacement.
 */
import React, { type FC } from 'react';
import { useEnrollment, useHasEnrollmentData } from '../_shared/hooks/use-enrollment';
import { HeadingSkeleton } from '../_shared/skeleton/patterns';
import { DynamicText } from '../_shared/dynamic-text';
import {
	createBlockHydrator,
	autoInitialize,
	datasetToNumber,
	datasetToBoolean,
	type BlockAttributes,
} from '../_shared/utils/block-hydration';

// ============================================================================
// Types
// ============================================================================

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

interface LoyaltyHeadingAttributes extends BlockAttributes {
	guestText: string;
	loggedInNotEnrolledText: string;
	enrolledText: string;
	level: HeadingLevel;
	textAlign?: string;
	enableAnimation?: boolean;
}

interface LoyaltyHeadingProps {
	attributes: LoyaltyHeadingAttributes;
	initialContent?: string;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Frontend loyalty heading component
 *
 * This component now only hydrates for enrolled users to update credits dynamically.
 * For guests and logged-in-not-enrolled users, the server-rendered content is used as-is.
 *
 * IMPORTANT: We preserve the server-rendered content by returning null initially
 * (which leaves the existing HTML intact) until the enrollment status is determined.
 */
const LoyaltyHeadingView: FC<LoyaltyHeadingProps> = ({ attributes, initialContent }) => {
	const { credits, creditsCurrency } = useEnrollment();
	const hasData = useHasEnrollmentData();

	// Show skeleton while loading (only if we don't have initial content yet)
	if (!hasData && !initialContent) {
		return (
			<span style={{ display: 'inline-block', minHeight: '1em', width: '100%' }}>
				<HeadingSkeleton
					level={attributes.level}
					ariaMessage="Loading your rewards balance..."
				/>
			</span>
		);
	}

	// Pass initialContent to prevent flash on first render
	// DynamicText will automatically prevent animation on initial render
	const enableAnimation = attributes.enableAnimation ?? true;
	const enrolledText = attributes.enrolledText || 'Welcome back!';

	return (
		<DynamicText
			text={enrolledText}
			replacements={{
				credits,
				credits_currency: creditsCurrency,
			}}
			animatedKeys={['credits']}
			enableAnimation={enableAnimation}
			initialContent={!hasData ? initialContent : undefined}
		/>
	);
};

// ============================================================================
// Initialization
// ============================================================================

/**
 * Initialize all loyalty heading blocks on the page
 *
 * Only hydrates blocks for enrolled users (those with data-enrollment-status="enrolled").
 * For guests and logged-in-not-enrolled users, the server-rendered content is used.
 *
 * IMPORTANT: We preserve the server-rendered content by only mounting React
 * after the enrollment store has resolved, preventing content flash.
 */
export const initLoyaltyHeadingBlocks = createBlockHydrator<LoyaltyHeadingAttributes>({
	// Only hydrate enrolled user blocks (with dynamic credits)
	selector: '[data-enrollment-status="enrolled"][data-enrolled-text]',

	extractAttributes: (element) => ({
		guestText: element.dataset.guestText || '',
		loggedInNotEnrolledText: element.dataset.loggedInNotEnrolledText || '',
		enrolledText: element.dataset.enrolledText || '',
		level: datasetToNumber(element.dataset.level, 2) as HeadingLevel,
		textAlign: element.dataset.textAlign || 'left',
		enableAnimation: datasetToBoolean(element.dataset.enableAnimation, true),
	}),

	render: (_element, attributes, initialContent) => (
		<LoyaltyHeadingView attributes={attributes} initialContent={initialContent} />
	),

	waitForStores: true,
	preserveInitialContent: true,
});

// Auto-initialize on DOM ready
autoInitialize(initLoyaltyHeadingBlocks);
